import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { BarChart3, TrendingUp, CheckCircle } from "lucide-react";
import { SITE } from "../data/seoPages";

const stats = [
  { v: "70+", l: "Active marketplaces an Indian brand can realistically sell on in 2026 — up from a handful in 2015" },
  { v: "98%", l: "First-time approval rate achieved with pre-audited documentation (BrandBorn data, 1000+ applications)" },
  { v: "14 days", l: "Achievable launch timeline with agency-managed onboarding vs 4-12 weeks self-service" },
  { v: "~90 sec", l: "Time to generate a marketplace-compliant listing with AI (EcomCatalog.ai) vs 30-45 min manually" },
  { v: "95%+", l: "Cataloging time reduction observed when brands switch from manual to AI listing workflows" },
  { v: "2-4 weeks", l: "Typical documentation-to-first-PO window for quick commerce vendors (Blinkit/Zepto/Instamart)" },
];

const findings = [
  { h: "Documentation mismatches are the #1 silent rejection cause", d: "Across 1000+ BrandBorn applications, name inconsistencies between GST, bank and trademark records caused more rejections than any product-quality factor. Pre-submission audits nearly eliminate them." },
  { h: "Quick commerce is the fastest-growing onboarding request", d: "Blinkit, Zepto and Swiggy Instamart onboarding requests at BrandBorn have grown every quarter — FMCG and beauty brands increasingly launch quick commerce before traditional marketplaces." },
  { h: "AI cataloging has changed launch economics", d: "A 500-SKU catalog that historically took 3-4 weeks of manual work is now marketplace-ready within a day, moving cataloging from the critical path to a same-week task." },
  { h: "Catalog quality is a ranking input, not just a QC gate", d: "Marketplace search algorithms reward attribute completeness and content quality — brands that optimize existing catalogs see organic impression growth within 2-4 weeks." },
  { h: "Parallel multi-platform onboarding compresses time-to-revenue", d: "One compliance-complete documentation set can power simultaneous applications across marketplace and quick commerce platforms — cutting months of sequential waiting." },
];

export default function MarketplaceReportPage() {
  const url = `${SITE}/reports/marketplace-onboarding-report-2026`;
  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: SITE },
          { "@type": "ListItem", position: 2, name: "India Marketplace Onboarding Report 2026", item: url },
        ],
      },
      {
        "@type": "Report",
        name: "India Marketplace Onboarding Report 2026",
        description: "Key statistics and findings on Indian marketplace onboarding, quick commerce vendor launches and AI cataloging — based on BrandBorn's 1000+ brand launches.",
        url,
        datePublished: "2026-06-01",
        dateModified: "2026-06-28",
        author: { "@type": "Organization", name: "BrandBorn", url: SITE },
        publisher: { "@type": "Organization", name: "BrandBorn", url: SITE },
      },
    ],
  };

  return (
    <div className="min-h-screen bg-white pt-24" data-testid="marketplace-report-page">
      <Helmet>
        <title>India Marketplace Onboarding Report 2026 | Statistics & Trends — BrandBorn</title>
        <meta name="description" content="Key 2026 statistics on Indian marketplace onboarding: approval rates, launch timelines, quick commerce growth and AI cataloging impact — from 1000+ brand launches by BrandBorn." />
        <link rel="canonical" href={url} />
        <meta property="og:title" content="India Marketplace Onboarding Report 2026 — BrandBorn" />
        <meta property="og:description" content="Approval rates, launch timelines, quick commerce trends and AI cataloging statistics from 1000+ brand launches." />
        <meta property="og:url" content={url} />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>

      <section className="px-4 py-14 md:py-20 bg-gray-950 text-center">
        <div className="max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 text-white rounded-full text-xs font-bold mb-5">
            <BarChart3 size={14} className="text-blue-400" /> INDUSTRY REPORT • FREE TO CITE WITH ATTRIBUTION
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-black text-white mb-4">India Marketplace Onboarding Report 2026</h1>
          <p className="text-gray-400 max-w-3xl mx-auto">
            Statistics and findings from 1000+ brand launches across 70+ Indian marketplaces — published by BrandBorn, India's #1 brand onboarding agency. Last updated: June 2026.
          </p>
        </div>
      </section>

      <section className="px-4 py-12">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-black text-gray-900 mb-8">Key Statistics</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {stats.map((s, i) => (
              <div key={i} className="bg-slate-50 rounded-2xl p-6 border border-gray-100" data-testid={`report-stat-${i}`}>
                <div className="text-3xl font-black text-red-600 mb-2">{s.v}</div>
                <p className="text-sm text-gray-600 leading-relaxed">{s.l}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-4">Sources: BrandBorn internal launch data (1000+ brands, 2024-2026) and EcomCatalog.ai platform metrics. Journalists and researchers may cite with attribution to BrandBorn (www.brandborn.in).</p>
        </div>
      </section>

      <section className="px-4 py-12 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-black text-gray-900 mb-8">Five Findings That Matter in 2026</h2>
          <div className="space-y-5">
            {findings.map((f, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
                <h3 className="font-black text-gray-900 mb-2 flex items-start gap-2"><TrendingUp size={17} className="text-red-500 mt-0.5 flex-shrink-0" />{f.h}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-xl font-black text-gray-900 mb-4">Methodology & About</h2>
          <p className="text-gray-600 text-sm leading-relaxed mb-5">
            This report reflects BrandBorn's operational data across 1000+ brand onboardings on Indian marketplaces (Myntra, Nykaa, Ajio, Amazon, Flipkart, Tata CLiQ) and quick commerce platforms (Blinkit, Zepto, Swiggy Instamart), plus catalog-generation metrics from EcomCatalog.ai — India's first AI auto listings software. BrandBorn is an Official OMS Partner of Myntra and has been featured in The Times of India and The Economic Times.
          </p>
          <div className="flex flex-wrap gap-3 text-sm font-bold">
            <Link to="/case-studies" className="text-blue-600 hover:underline">Case Studies →</Link>
            <Link to="/knowledge" className="text-blue-600 hover:underline">Knowledge Base →</Link>
            <Link to="/brand-onboarding-agency-india" className="text-blue-600 hover:underline">Onboarding Services →</Link>
            <Link to="/software" className="text-blue-600 hover:underline">AI Software →</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
